import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { SkloCtx, skloMaterial } from '../ui/Sklo';

/**
 * Hlavička detailu: dvě akce podle stavu a pod „…" zbytek.
 *
 * Doklady měly v hlavičce šest tlačítek (včetně Smazat vedle Schválit),
 * Pokladna tři, Suroviny dvě. Na mobilu z šesti byly tři řádky tlačítek nad
 * obsahem. Norma povoluje tři ovladače — tahle komponenta to drží za moduly,
 * ať se to příště nerozjede znovu.
 *
 * Panel jde do portálu: hlavička detailu je `sticky` s vlastním `z-index`
 * a rozbalená nabídka uvnitř ní by se ořízla o její hranu.
 *
 * @param {Array<{id, label, Icon, onClick, primarni?, barva?, skrytNaMobilu?}>} akce
 *        Pořadí rozhoduje: co se nevejde do `kolikVidet`, spadne do nabídky.
 */
export default function NabidkaAkci({
    akce = [], kolikVidet = 2, isMobile = false, isDark, barva = '#3B82F6',
    /**
     * `true` = na povrchu nezůstane nic, všechno jde do nabídky pod „⋯".
     *
     * Pro hlavičky, které si primární akci kreslí samy (mobilní doklad má
     * Schválit přes celou šířku v palcové zóně) a od téhle komponenty chtějí
     * už jen zbytek. Bez toho by se i s `kolikVidet={0}` protlačila jedna
     * akce na povrch — mobilní větev má limit napevno 1.
     */
    jenNabidka = false,
}) {
    const [otevreno, setOtevreno] = useState(false);
    const [pozice, setPozice] = useState(null);
    const tlacitkoRef = useRef(null);
    const skloHlavicky = useContext(SkloCtx) && isMobile;
    const panelRef = useRef(null);

    const pouzitelne = akce.filter(a => a && !(isMobile && a.skrytNaMobilu));
    // Primární akce jdou na povrch vždy, zbytek podle pořadí.
    const primarni = pouzitelne.filter(a => a.primarni);
    const ostatni = pouzitelne.filter(a => !a.primarni);
    const limit = Math.max(0, (isMobile ? 1 : kolikVidet) - primarni.length);
    const videt = jenNabidka ? [] : [...primarni, ...ostatni.slice(0, limit)];
    const schovane = jenNabidka ? pouzitelne : ostatni.slice(limit);

    useLayoutEffect(() => {
        if (!otevreno) return;
        const r = tlacitkoRef.current?.getBoundingClientRect();
        if (!r) return;
        const sirka = 232;
        setPozice({
            left: Math.min(Math.max(8, r.right - sirka), window.innerWidth - sirka - 8),
            top: r.bottom + 6,
        });
    }, [otevreno]);

    useEffect(() => {
        if (!otevreno) return;
        const mimo = (e) => {
            if (tlacitkoRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setOtevreno(false);
        };
        const esc = (e) => { if (e.key === 'Escape') setOtevreno(false); };
        document.addEventListener('mousedown', mimo);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', mimo); document.removeEventListener('keydown', esc); };
    }, [otevreno]);

    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';

    const tlacitko = (a) => (
        <button key={a.id} onClick={a.onClick} title={a.label}
            className="inline-flex items-center gap-1.5 rounded-lg font-semibold"
            style={{
                minHeight: isMobile ? 40 : 34, padding: '0 12px', fontSize: 13,
                border: a.primarni ? 'none' : `1px solid ${hranice}`,
                background: a.primarni ? (a.barva || barva) : 'transparent',
                color: a.primarni ? '#fff' : (a.barva || 'var(--text-secondary)'),
                cursor: 'pointer', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent',
            }}>
            <a.Icon size={14} /> {a.label}
        </button>
    );

    return (
        <div className="flex items-center gap-1.5" style={{ flexShrink: 0 }}>
            {videt.map(tlacitko)}

            {schovane.length > 0 && (
                <button ref={tlacitkoRef} onClick={() => setOtevreno(o => !o)}
                    aria-label="Další akce" aria-expanded={otevreno}
                    className="inline-flex items-center justify-center rounded-lg"
                    style={skloHlavicky ? {
                        // V hlavičce ve skle (mobil) skleněný kruh jako ostatní ovládání
                        width: 44, height: 44, borderRadius: 999, border: 'none', flexShrink: 0,
                        ...skloMaterial(isDark),
                        color: otevreno ? barva : 'var(--text-primary)',
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    } : {
                        // Sama v hlavičce je to ikona app baru, ne ovladač v řadě
                        // tlačítek — rámeček by z ní udělal čtvrtý tvar na obrazovce.
                        minHeight: jenNabidka ? 44 : (isMobile ? 40 : 34),
                        width: jenNabidka ? 44 : (isMobile ? 40 : 34),
                        border: jenNabidka ? 'none' : `1px solid ${hranice}`,
                        background: otevreno && !jenNabidka ? `${barva}14` : 'transparent',
                        color: otevreno ? barva : (jenNabidka ? 'var(--text-primary)' : 'var(--text-secondary)'),
                        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    }}>
                    <MoreHorizontal size={skloHlavicky ? 20 : (jenNabidka ? 22 : 17)} />
                </button>
            )}

            {otevreno && pozice && createPortal(
                <div ref={panelRef} role="menu"
                    style={{
                        position: 'fixed', left: pozice.left, top: pozice.top, width: 232, zIndex: 1200,
                        borderRadius: 12, overflow: 'hidden', padding: 4,
                        background: isDark ? 'rgba(24,22,32,0.96)' : 'rgba(255,255,255,0.97)',
                        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
                        border: `1px solid ${hranice}`,
                        boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
                    }}>
                    {schovane.map(a => (
                        <button key={a.id} role="menuitem"
                            onClick={() => { setOtevreno(false); a.onClick?.(); }}
                            className="flex items-center gap-2.5 w-full text-left rounded-lg"
                            style={{
                                minHeight: 42, padding: '0 10px', fontSize: 13.5, fontWeight: 500,
                                color: a.barva || 'var(--text-primary)', background: 'transparent', cursor: 'pointer',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            <a.Icon size={15} style={{ flexShrink: 0, opacity: 0.85 }} /> {a.label}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </div>
    );
}
